import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';

/**
 * Locker — the door system at a facility, and the codes installed in it.
 *
 * **Its fields depend on its type.** `getPropertiesByTypeId` answers a list of
 * property names for the locker's type, and only those are editable; the old page
 * filters the payload by that list and this one does the same, including its two
 * naming rules — the payload key is the property name with a lower-case first
 * letter, and `randomCodeLenght` (the misspelling is the backend's) is shown as
 * "Code length".
 *
 * Two capabilities decide what can be done: `supportsCodeInstall` and
 * `supportsCodeListing`. The old page also refuses to install on anything but a
 * Nox locker, after you have opened the form — here that is checked before, so
 * the action is simply closed with the reason in its title.
 *
 * Codes are what this page is for operationally: installing one lets somebody
 * through a door, removing one stops them.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

// The property list is names; the payload key is the same with a lower-case
// first letter.
const toKey = name => name.charAt(0).toLowerCase() + name.slice(1);
const label = name => (name.toLowerCase() === 'randomcodelenght' ? 'Code length' : name);

const LockerInternal = ({ api }) => {
  const { lockerId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();

  const [locker, setLocker] = useState(null);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(null);
  const [codes, setCodes] = useState(undefined);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    let data = null;
    try {
      const answer = await api.lockers.getLockerById(lockerId);
      data = answer?.data || answer;
      if (!data || !data.id) {
        setFailed(true);
        return;
      }
      setLocker(data);
      setForm({ ...data });
      setFailed(false);
    } catch (error) {
      setFailed(true);
      return;
    }

    try {
      const answer = await api.lockers.getPropertiesByTypeId(data.lockerTypeId ?? data.typeId);
      setProperties(Array.isArray(answer?.data) ? answer.data : []);
    } catch (error) {
      // Without the list nothing type-specific is editable, which is safer than
      // guessing which keys belong to this type.
      setProperties([]);
    }
  }, [api, lockerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/lockers">← All lockers</BackToList>
        <div className="next-note next-note--error">
          Could not load this locker.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!locker || !form) return <div className="next-page next-muted">Loading…</div>;

  const isNox = String(locker.lockerType || '').toLowerCase().trim() === 'nox';
  const canInstall = !!locker.supportsCodeInstall && isNox;
  const canList = !!locker.supportsCodeListing;

  const dirty = properties.some(name => form[toKey(name)] !== locker[toKey(name)])
    || form.name !== locker.name;

  const save = async () => {
    setBusy(true);
    try {
      const response = await api.lockers.updateLocker(form);
      if (response?.success === false) {
        setNote({ tone: 'error', text: `Unable to update the locker. ${response.message || ''}`.trim() });
        return;
      }
      setNote({ tone: 'info', text: 'Locker saved.' });
      await load();
    } catch (error) {
      setNote({ tone: 'error', text: error?.message || 'Unable to update the locker.' });
    } finally {
      setBusy(false);
    }
  };

  const showCodes = async () => {
    setCodes(null);
    try {
      const answer = await api.lockers.getInstalledCodes(lockerId);
      setCodes(Array.isArray(answer?.data) ? answer.data : []);
    } catch (error) {
      setCodes([]);
      setNote({ tone: 'error', text: 'Unable to read the installed codes.' });
    }
  };

  const installCode = () => ask({
    kind: 'input',
    title: 'Install a code',
    context: `Install code · ${locker.name || lockerId}`,
    body: 'The code opens this door for the duration you give it, starting from the time you give it. Anyone with the code can get in during that window.',
    label: 'Code',
    placeholder: '4821',
    hint: '4 to 10 digits. It works from now, for 24 hours',
    mono: true,
    confirmLabel: 'Install',
    onConfirm: async code => {
      // The backend accepts only 4-10 plain digits (the code goes into the door
      // system's command); said here before the request rather than after it.
      if (!/^[0-9]{4,10}$/.test(code.trim())) {
        setNote({ tone: 'error', text: 'The code must be 4 to 10 digits, nothing else.' });
        throw new Error('invalid code');
      }
      // The endpoint takes a start time and a duration; the dialog collects the
      // code, and these default to "from now, for a day" — the same shape the old
      // form submits, with values a reader can see and change afterwards.
      const start = new Date().toISOString();
      const response = await api.lockers.installCode(lockerId, start, 1440, code.trim());
      if (response?.success === false) {
        setNote({ tone: 'error', text: response.message || 'The code was not installed.' });
        throw new Error('install failed');
      }
      setNote({ tone: 'info', text: `Code installed from now, for 24 hours.` });
      if (canList) await showCodes();
    }
  });

  const removeCode = reference => ask({
    kind: 'danger',
    title: 'Remove this code?',
    context: `Remove code · ${locker.name || lockerId}`,
    body: 'Whoever has it stops being able to open the door, immediately.',
    confirmLabel: 'Remove code',
    onConfirm: async () => {
      const response = await api.lockers.removeCode(lockerId, reference);
      if (response?.success === false) {
        setNote({ tone: 'error', text: response.message || 'The code was not removed.' });
        throw new Error('remove failed');
      }
      setNote({ tone: 'info', text: 'Code removed.' });
      await showCodes();
    }
  });

  const removeLocker = () => ask({
    kind: 'danger',
    title: `Delete ${locker.name || 'this locker'}?`,
    context: `Delete locker · ${locker.facilityName || ''}`.trim(),
    body: 'The facility loses its door system here: codes stop being installed for new bookings, and anything that relies on it stops working.',
    warning: 'This cannot be undone from the admin.',
    confirmLabel: 'Delete locker',
    onConfirm: async () => {
      const response = await api.lockers.deleteLocker(lockerId);
      if (response?.success === false) {
        setNote({ tone: 'error', text: response.message || 'The locker was not deleted.' });
        throw new Error('delete failed');
      }
      navigate('/next/lockers');
    }
  });

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/lockers">← All lockers</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{locker.name || 'Locker'}</h1>
          {locker.lockerType && <span className="next-pill next-pill--neutral">{locker.lockerType}</span>}
        </div>
        <span className="next-detail__id">{locker.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head">
              <h2>Locker</h2>
              <span className="next-muted">
                {properties.length
                  ? `${properties.length} setting${properties.length === 1 ? '' : 's'} for this type`
                  : 'no type-specific settings'}
              </span>
            </div>

            <div className="next-form__grid">
              <label className="next-filter">
                <span className="next-filter__label">Name</span>
                <input
                  className="next-input"
                  value={form.name || ''}
                  onChange={event => setForm({ ...form, name: event.target.value })}
                />
              </label>

              {/* Only what this locker type actually has. */}
              {properties.map(name => (
                <label className="next-filter" key={name}>
                  <span className="next-filter__label">{label(name)}</span>
                  <input
                    className="next-input"
                    value={form[toKey(name)] ?? ''}
                    onChange={event => setForm({ ...form, [toKey(name)]: event.target.value })}
                  />
                </label>
              ))}
            </div>

            <div className="next-form__actions">
              <button
                type="button"
                className="next-btn next-btn--primary"
                disabled={!dirty || busy}
                onClick={save}
              >
                Save changes
              </button>
              <button
                type="button"
                className="next-btn"
                disabled={!dirty || busy}
                onClick={() => setForm({ ...locker })}
              >
                Discard
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Facility</h2></div>
            <div className="next-facts next-facts--single">
              <div className="next-fact">
                <span className="next-fact__label">Facility</span>
                <span className="next-fact__value">{locker.facilityName || '—'}</span>
              </div>
              <div className="next-fact">
                <span className="next-fact__label">Facility id</span>
                <span className="next-fact__value">
                  {locker.facilityId
                    ? <Guid value={locker.facilityId} to={`/next/facility/${locker.facilityId}`} />
                    : <span className="next-muted">—</span>}
                </span>
              </div>
            </div>
          </section>

          {canList && (
            <section className="next-card">
              <div className="next-card__head">
                <h2>Installed codes</h2>
                <span className="next-card__spacer" />
                <button type="button" className="next-btn next-btn--sm" onClick={showCodes}>
                  {codes === undefined ? 'Show codes' : 'Refresh'}
                </button>
              </div>

              {codes === undefined && (
                <p className="next-table__note next-muted">
                  Reading the codes asks the door system, so it is not done on every visit.
                </p>
              )}
              {codes === null && <p className="next-table__note next-muted">Loading…</p>}
              {Array.isArray(codes) && codes.length === 0 && (
                <p className="next-table__note next-muted">No code is installed.</p>
              )}

              {Array.isArray(codes) && codes.length > 0 && (
                <div className="next-table-wrap">
                  <table className="next-table next-table--wide next-table--stack">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Code</th>
                        <th>From</th>
                        <th>Duration</th>
                        <th className="next-table__right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map(entry => (
                        <tr key={entry.reference || entry.Reference}>
                          <td data-label="">{entry.reference || entry.Reference || '—'}</td>
                          <td data-label="Code" className="next-mono-list">{entry.code || entry.Code || '—'}</td>
                          <td data-label="From" className="next-nowrap">
                            {fmtDate(entry.dateTime || entry.DateTime || entry.from)}
                          </td>
                          <td data-label="Duration">
                            {(entry.duration ?? entry.Duration) != null
                              ? `${entry.duration ?? entry.Duration} min`
                              : '—'}
                          </td>
                          <td className="next-table__right" data-label="">
                            <button
                              type="button"
                              className="next-btn next-btn--sm next-btn--danger"
                              onClick={() => removeCode(entry.reference || entry.Reference)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button
                type="button"
                className="next-actionlist__item"
                disabled={!canInstall}
                title={canInstall
                  ? undefined
                  : (locker.supportsCodeInstall
                    ? 'Installing codes only works on a Nox locker'
                    : 'This locker does not support installing codes')}
                onClick={installCode}
              >
                Install a code
              </button>
              <button
                type="button"
                className="next-actionlist__item"
                disabled={!canList}
                title={canList ? undefined : 'This locker does not support listing codes'}
                onClick={showCodes}
              >
                Show installed codes
              </button>
              <button
                type="button"
                className="next-actionlist__item next-actionlist__item--danger"
                onClick={removeLocker}
              >
                Delete locker
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                locker.facilityId && { label: 'Facility', to: `/next/facility/${locker.facilityId}` },
                locker.facilityId && {
                  label: 'Signups here',
                  hint: 'codes are installed for these',
                  to: `/next/signups?facilityId=${locker.facilityId}`
                },
                { label: 'Salto master codes', href: '/salto-master-codes' }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

LockerInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Locker = withApi(LockerInternal);

Locker.requiresLogin = true;

export default Locker;
